import { CSSProperties, memo, useCallback, useEffect, useRef, useState } from 'react';
import { RgbaColor, RgbaColorPicker } from 'react-colorful';
import chroma from 'chroma-js';
import { debounce } from 'underscore';
import { ColorPickerProps, OnColorPickerChangeResult } from './color-picker.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import EyeDropper from './eye-dropper/eye-dropper';

import './color-picker-simple.css';

const ColorPickerSimple = memo(
  ({
    initialColor,
    value,
    onChange,
    containerClassName,
    pickerClassName,
    style,
    saturationPickerHeight,
    width,
    boxShadow,
  }: ColorPickerProps) => {
    const [color, setColor] = useState<{ hex: string; rgba: RgbaColor }>();

    useEffect(() => {
      if (color) return;

      const isValidColor = chroma.valid(initialColor);

      if (!initialColor || !isValidColor) {
        const startingColor = { r: 50, g: 100, b: 150, a: 1 };
        setColor({
          rgba: startingColor,
          hex: chroma(`rgba(${startingColor.r}, ${startingColor.g}, ${startingColor.b}, ${startingColor.a})`).hex(),
        });
        return;
      }

      const hex = chroma(initialColor).hex();
      const rgba = chroma(initialColor).rgba();
      setColor({
        rgba: {
          r: rgba[0],
          g: rgba[1],
          b: rgba[2],
          a: rgba[3],
        },
        hex: hex,
      });
    }, [color, initialColor]);

    /* if value is used then it's a controlled component and we should update color based on that value */
    useEffect(() => {
      if (value === undefined) return;

      const isValidColor = chroma.valid(value);

      if (!value || !isValidColor) {
        const startingColor = { r: 50, g: 100, b: 150, a: 1 };
        setColor({
          rgba: startingColor,
          hex: chroma(`rgba(${startingColor.r}, ${startingColor.g}, ${startingColor.b}, ${startingColor.a})`).hex(),
        });
        return;
      }

      const hex = chroma(value).hex();
      const rgba = chroma(value).rgba();
      setColor({
        rgba: {
          r: rgba[0],
          g: rgba[1],
          b: rgba[2],
          a: rgba[3],
        },
        hex: hex,
      });
    }, [value]);

    const debouncedChange = useRef(
      debounce((color: OnColorPickerChangeResult) => {
        onChange?.(color);
      }, 100)
    ).current;

    const handleColorPickerChange = useCallback(
      (color: RgbaColor) => {
        const rgbaString = `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
        const hex = chroma(rgbaString).hex();
        setColor({
          rgba: color,
          hex,
        });
        debouncedChange?.({
          rgba: rgbaString,
          hex,
          rgb: `rgb(${color.r}, ${color.g}, ${color.b})`,
          opacity: color.a,
        });
      },
      [debouncedChange]
    );

    const handleCustomSelectColor = useCallback(
      (color: string) => {
        const asRgba = chroma(color).rgba();
        handleColorPickerChange({
          r: asRgba[0],
          g: asRgba[1],
          b: asRgba[2],
          a: asRgba[3],
        });
      },
      [handleColorPickerChange]
    );

    const eyeDropperAvailable = typeof window !== 'undefined' && !!window?.EyeDropper;

    return (
      <div
        className={`color-picker-simple-container ${containerClassName || ''}`}
        style={
          {
            ...(saturationPickerHeight ? { '--saturationPickerHeight': saturationPickerHeight } : {}),
            ...(width ? { '--colorPickerWidth': width } : {}),
            ...(boxShadow ? { '--colorPickerBoxShadow': boxShadow } : {}),
          } as CSSProperties
        }
      >
        <div className="rgba-picker-container">
          <RgbaColorPicker
            color={color?.rgba}
            onChange={handleColorPickerChange}
            className={`color-picker ${pickerClassName || ''} ${eyeDropperAvailable ? 'eye-dropper-available' : ''}`}
            style={style || {}}
          />

          <OptionalComponent display={eyeDropperAvailable}>
            <div className="color-picker-eye-dropper-container">
              <EyeDropper onSelectColor={handleCustomSelectColor} />
            </div>
          </OptionalComponent>

          <div
            style={{ backgroundColor: `rgba(${color?.rgba.r}, ${color?.rgba.g}, ${color?.rgba.b}, ${color?.rgba.a})` }}
            className="square-32 radius-4 color-picker-display-block"
          />
        </div>
      </div>
    );
  }
);

export default ColorPickerSimple;
